import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  TextField, MenuItem, Grid, Pagination
} from '@mui/material';
import {
  Phone, WhatsApp, Assessment
} from '@mui/icons-material';
import { api } from '../context/AuthContext';

const CreditUsagePage = () => {
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 1,
    limit: 10
  });
  const [filters, setFilters] = useState({
    type: ''
  });

  useEffect(() => {
    fetchUsage();
  }, [filters, page]);

  const fetchUsage = async () => {
    setLoading(true);
    try {
      let url = `/applications/comm-center/usage?page=${page}&limit=10`;

      if (filters.type) {
        url += `&type=${filters.type}`;
      }

      const res = await api.get(url);

      setUsage(res.data.data || []);

      setPagination({
        totalRecords: res.data.pagination?.totalRecords || 0,
        totalPages: res.data.pagination?.totalPages || 1,
        limit: res.data.pagination?.limit || 10
      });
    } catch (err) {
      console.error('Failed to fetch usage:', err);
    } finally {
      setLoading(false);
    }
  };

  const startRecord =
      pagination.totalRecords === 0
          ? 0
          : (page - 1) * pagination.limit + 1;

  const endRecord = Math.min(
      page * pagination.limit,
      pagination.totalRecords
  );

  return (
    <Box sx={{ p: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: "#1E3A8A", fontWeight: "bold", fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Credit Usage
                </Typography>
        <Typography variant="body1" color="text.secondary">Track how calling and WhatsApp credits are consumed</Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: '16px' }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Communication Type"
                value={filters.type}
                onChange={(e) => {
                  setPage(1);
                  setFilters({ ...filters, type: e.target.value });
                }}
                size="small"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="voice">Voice Call</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <Table>

          <TableHead sx={{ bgcolor: '#F8FAFC' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Communication Type</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Credits Consumed</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} sx={{ my: 2 }} /></TableCell></TableRow>
            ) : usage.length > 0 ? usage.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                    <Typography variant="body2">{new Date(row.created_at).toLocaleDateString()}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(row.created_at).toLocaleTimeString()}</Typography>
                </TableCell>
                <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {row.type === 'voice' ? <Phone sx={{ fontSize: 16, color: '#10B981' }} /> : <WhatsApp sx={{ fontSize: 16, color: '#2563EB' }} />}
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{row.type === 'voice' ? 'Voice Call' : 'WhatsApp'}</Typography>
                    </Box>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.first_name} {row.last_name}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{row.credits_consumed}</TableCell>
                <TableCell>
                  <Chip 
                    label={row.status || 'Success'} 
                    size="small" 
                    color={row.status === 'failed' ? 'error' : 'success'} 
                    variant="soft"
                    sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                  />
                </TableCell>
              </TableRow>
            )) : (
                <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No usage records found</Typography>
                    </TableCell>
                </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 2
          }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {startRecord} to {endRecord} of {pagination.totalRecords} entries
        </Typography>

        <Pagination
            page={page}
            count={pagination.totalPages}
            onChange={(event, value) => setPage(value)}
            color="primary"
            shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default CreditUsagePage;
